"use client"

import * as React from "react"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ArrowRight, ExternalLink } from "lucide-react"
import { NavPillar, NavItem } from "@/lib/navigation"
import { cn } from "@/lib/utils"

interface NavDropdownProps {
    pillar: NavPillar
    isActivePillar: boolean
    isOpen: boolean
    onOpen: () => void
    onClose: () => void
}

const badgeStyles = {
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    purple: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    blue: "bg-[#C88246]/15 text-[#D8B478] border-[#C88246]/30",
}

export function NavDropdown({
    pillar,
    isActivePillar,
    isOpen,
    onOpen,
    onClose
}: NavDropdownProps) {
    const PillarIcon = pillar.icon

    return (
        <div
            className="relative"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
        >
            {/* Top-Level Pillar Trigger */}
            <button
                type="button"
                onClick={() => (isOpen ? onClose() : onOpen())}
                aria-expanded={isOpen}
                className={cn(
                    "flex items-center gap-1.5 py-2 px-3 text-[11px] font-bold uppercase tracking-[0.16em] transition-all rounded-lg font-sans outline-none group cursor-pointer",
                    isOpen || isActivePillar
                        ? "text-amber-400 bg-amber-500/10 border border-amber-500/20 shadow-sm shadow-amber-500/10"
                        : "text-slate-300 hover:text-white hover:bg-slate-900/60 border border-transparent"
                )}
            >
                <PillarIcon className={cn(
                    "w-3.5 h-3.5 transition-colors",
                    isOpen || isActivePillar ? "text-amber-400" : "text-slate-400 group-hover:text-amber-400"
                )} />
                <span>{pillar.label}</span>
                <ChevronDown className={cn(
                    "w-3 h-3 text-slate-500 transition-transform duration-200",
                    isOpen && "rotate-180 text-amber-400"
                )} />
            </button>

            {/* Dropdown Menu Container */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.16, ease: "easeOut" }}
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 w-[580px] max-w-[90vw] pointer-events-auto"
                    >
                        {/* Frosted Obsidian Glass Card */}
                        <div className="rounded-2xl bg-slate-950/95 backdrop-blur-2xl border border-slate-800/90 shadow-2xl shadow-black/90 p-4 overflow-hidden relative">
                            {/* Runic Top Ambient Accent */}
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent pointer-events-none" />

                            <div className="grid grid-cols-12 gap-3">
                                {/* Left Feature Spotlight (If Defined) */}
                                {pillar.featured && (
                                    <div className="col-span-5 bg-gradient-to-b from-slate-900/90 to-slate-950 p-4 rounded-xl border border-slate-800/80 flex flex-col justify-between group/feat relative overflow-hidden">
                                        <div className="absolute -top-12 -right-12 w-28 h-28 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />

                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <span className="text-[9px] font-bold uppercase tracking-widest text-amber-400 font-cinzel">
                                                    Spotlight Feature
                                                </span>
                                                {pillar.featured.badge && (
                                                    <span className={cn(
                                                        "text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded border tracking-wider",
                                                        badgeStyles[pillar.featured.badge.variant]
                                                    )}>
                                                        {pillar.featured.badge.text}
                                                    </span>
                                                )}
                                            </div>

                                            <h4 className="text-sm font-bold text-white font-cinzel group-hover/feat:text-amber-400 transition-colors flex items-center gap-1.5">
                                                <pillar.featured.icon className="w-4 h-4 text-amber-400 shrink-0" />
                                                {pillar.featured.label}
                                            </h4>

                                            <p className="text-[11px] text-slate-400 leading-relaxed mt-2 font-sans line-clamp-3">
                                                {pillar.featured.description}
                                            </p>
                                        </div>

                                        <Link
                                            href={pillar.featured.href}
                                            target={pillar.featured.isExternal ? "_blank" : undefined}
                                            rel={pillar.featured.isExternal ? "noopener noreferrer" : undefined}
                                            onClick={onClose}
                                            className="mt-4 inline-flex items-center justify-between w-full py-2 px-3 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase tracking-wider transition-all"
                                        >
                                            <span>Explore Feature</span>
                                            {pillar.featured.isExternal ? (
                                                <ExternalLink className="w-3 h-3" />
                                            ) : (
                                                <ArrowRight className="w-3 h-3 group-hover/feat:translate-x-0.5 transition-transform" />
                                            )}
                                        </Link>
                                    </div>
                                )}

                                {/* Right Items Grid */}
                                <div className={cn("space-y-1", pillar.featured ? "col-span-7" : "col-span-12")}>
                                    <div className="px-2 pb-1.5 flex items-center justify-between border-b border-slate-800/60 mb-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-cinzel">
                                            {pillar.label} Compendium
                                        </span>
                                        <span className="text-[10px] text-slate-500 font-mono">
                                            {pillar.items.length} Destinations
                                        </span>
                                    </div>

                                    <div className="space-y-0.5 max-h-[380px] overflow-y-auto pr-1">
                                        {pillar.items.map((item) => {
                                            const ItemIcon = item.icon
                                            return (
                                                <Link
                                                    key={item.href}
                                                    href={item.href}
                                                    target={item.isExternal ? "_blank" : undefined}
                                                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                                                    onClick={onClose}
                                                    className="flex items-start gap-3 p-2 rounded-xl hover:bg-slate-900/90 border border-transparent hover:border-slate-800/80 transition-all group/item"
                                                >
                                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0 text-slate-400 group-hover/item:text-amber-400 group-hover/item:border-amber-500/30 group-hover/item:bg-amber-500/5 transition-all shadow-sm">
                                                        <ItemIcon className="w-4 h-4" />
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="text-xs font-bold text-slate-200 group-hover/item:text-white transition-colors truncate">
                                                                {item.label}
                                                            </span>
                                                            {item.badge && (
                                                                <span className={cn(
                                                                    "text-[8px] font-extrabold uppercase px-1.5 py-0.2 rounded border tracking-wider shrink-0",
                                                                    badgeStyles[item.badge.variant]
                                                                )}>
                                                                    {item.badge.text}
                                                                </span>
                                                            )}
                                                            {item.isExternal && (
                                                                <ExternalLink className="w-2.5 h-2.5 text-slate-500 shrink-0" />
                                                            )}
                                                        </div>

                                                        <p className="text-[10px] text-slate-400 leading-snug truncate mt-0.5 font-sans group-hover/item:text-slate-300">
                                                            {item.description}
                                                        </p>
                                                    </div>
                                                </Link>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
